// src/pages/RawMaterialPurchases/RawMaterialPurchases.jsx

import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { collection, onSnapshot, query, orderBy, doc, getDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../context/AuthContext';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import { font } from '../Orders/Roboto-Regular-font';

// --- STYLES ---
const styles = {
    container: { fontFamily: '"Inter", sans-serif', maxWidth: '1200px', margin: '30px auto', padding: '0 20px' },
    header: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '30px' },
    title: { fontSize: '28px', fontWeight: '700', color: '#1e293b', margin: 0 },

    // Stats
    statsContainer: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '20px', marginBottom: '30px' },
    statCard: { background: 'white', padding: '24px', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', display: 'flex', flexDirection: 'column', border: '1px solid #e2e8f0' },
    statTitle: { fontSize: '14px', color: '#64748b', fontWeight: '600', marginBottom: '8px', textTransform: 'uppercase', letterSpacing: '0.05em' },
    statValue: { fontSize: '32px', fontWeight: '800', color: '#0f172a' },
    statIcon: { width: '48px', height: '48px', borderRadius: '12px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '16px', fontSize: '24px' },

    // Actions
    actionButton: { padding: '12px 24px', border: 'none', background: 'linear-gradient(135deg, #06C167, #8b5cf6)', color: 'white', borderRadius: '10px', cursor: 'pointer', fontSize: '15px', fontWeight: '600', textDecoration: 'none', display: 'flex', alignItems: 'center', gap: '8px', boxShadow: '0 4px 6px -1px rgba(99, 102, 241, 0.3)' },

    // Table Box
    box: { background: 'white', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)', border: '1px solid #e2e8f0', overflow: 'hidden' },
    tableHeader: { padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' },
    tableTitle: { fontSize: '18px', fontWeight: '700', color: '#0f172a', margin: 0 },

    // Table
    table: { width: '100%', borderCollapse: 'collapse', textAlign: 'left' },
    th: { padding: '16px 24px', background: '#f8fafc', color: '#475569', fontWeight: '600', fontSize: '13px', textTransform: 'uppercase', borderBottom: '1px solid #e2e8f0' },
    td: { padding: '16px 24px', borderBottom: '1px solid #f1f5f9', color: '#334155', fontSize: '14px' },
    tr: { transition: 'background 0.1s', cursor: 'pointer' },

    // Detail View Styles
    detailContainer: { background: 'white', padding: '40px', borderRadius: '16px', boxShadow: '0 4px 6px -1px rgba(0, 0, 0, 0.1)', maxWidth: '900px', margin: '40px auto' },
    detailHeader: { display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '40px', borderBottom: '2px solid #f1f5f9', paddingBottom: '20px' },
    detailTitle: { fontSize: '24px', fontWeight: 'bold', color: '#0f172a' },
    backLink: { display: 'inline-flex', alignItems: 'center', gap: '6px', color: '#06C167', fontWeight: '600', textDecoration: 'none', marginBottom: '20px', cursor: 'pointer', fontSize: '14px' },

    infoGrid: { display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '24px', marginBottom: '40px', background: '#f8fafc', padding: '24px', borderRadius: '12px' },
    infoItem: { display: 'flex', flexDirection: 'column', gap: '4px' },
    infoLabel: { fontSize: '13px', color: '#64748b', fontWeight: '600', textTransform: 'uppercase' },
    infoValue: { fontSize: '16px', fontWeight: '600', color: '#0f172a' },

    statusBadge: { padding: '4px 10px', borderRadius: '20px', fontSize: '12px', fontWeight: '600', background: '#dcfce7', color: '#166534' }
};

// --- YARDIMCI FONKSİYONLAR ---
const safeString = (value, fallback = '-') => (value === null || typeof value === 'undefined' || value === '') ? fallback : String(value).trim();
const safeNumber = (value, fallback = 0) => (typeof value !== 'number' || isNaN(value)) ? fallback : value;
const sanitizeForPDF = (text) => {
    if (typeof text !== 'string') return text;
    return text.replace(/ı/g, 'i').replace(/İ/g, 'I').replace(/ğ/g, 'g').replace(/Ğ/g, 'G').replace(/ü/g, 'u').replace(/Ü/g, 'U').replace(/ş/g, 's').replace(/Ş/g, 'S').replace(/ö/g, 'o').replace(/Ö/g, 'O').replace(/ç/g, 'c').replace(/Ç/g, 'C');
};

const formatCurrency = (amount) => {
    return new Intl.NumberFormat('tr-TR', { style: 'currency', currency: 'TRY' }).format(amount);
};

const formatDate = (date) => {
    if (!date) return 'Bilinmiyor';
    // Handle Firestore Timestamp
    if (date.seconds) {
        return new Date(date.seconds * 1000).toLocaleDateString('tr-TR');
    }
    // Handle JS Date or ISO String
    const d = new Date(date);
    if (isNaN(d.getTime())) return 'Hatalı Tarih';
    return d.toLocaleDateString('tr-TR');
};

// --- PDF OLUŞTURMA ---
const generateRawMaterialPurchasePDF = (companyInfo, purchase, rawMaterials, withVisuals = true) => {
    try {
        const getRawMaterialBarcode = (rawMaterialId) => rawMaterials.find(p => p.id === rawMaterialId)?.barcode || '-';
        const doc = new jsPDF();

        // Add Turkish font support
        doc.addFileToVFS('Roboto-Regular.ttf', font);
        doc.addFont('Roboto-Regular.ttf', 'Roboto', 'normal');
        doc.setFont('Roboto');

        doc.setFontSize(22);
        doc.setTextColor(44, 62, 80);
        doc.text(sanitizeForPDF(safeString(companyInfo?.companyName, "Firma Adı")), 14, 22);
        doc.setFontSize(18);
        doc.setTextColor(149, 165, 166);
        doc.text(sanitizeForPDF("HAMMADDE SATIN ALMA"), 200, 22, { align: 'right' });
        doc.line(14, 28, 200, 28);

        doc.setFontSize(10);
        doc.text(sanitizeForPDF("Tedarikçi Firma Bilgileri"), 14, 40);
        doc.setFontSize(9);
        doc.text(sanitizeForPDF(safeString(purchase.supplierName)), 14, 46);

        doc.setFontSize(10);
        doc.text(sanitizeForPDF("Kayıt No:"), 200, 40, { align: 'right' });
        doc.setFontSize(9);
        doc.text(purchase.id.substring(0, 8).toUpperCase(), 200, 46, { align: 'right' });

        doc.setFontSize(10);
        doc.text("Tarih:", 200, 52, { align: 'right' });
        doc.setFontSize(9);
        doc.text(formatDate(purchase.purchaseDate), 200, 58, { align: 'right' });

        const tableColumn = ["Stok Kodu", sanitizeForPDF("Hammadde Adı"), "Miktar", "Birim Fiyat", "Toplam"];
        const tableRows = (purchase.lineItems || []).map(item => [
            getRawMaterialBarcode(item.productId),
            sanitizeForPDF(item.name || item.productName || 'İsimsiz'),
            `${item.quantity || 0} ${item.unit || 'Adet'}`,
            `${safeNumber(item.price).toFixed(2)} TL`,
            `${safeNumber(item.total).toFixed(2)} TL`
        ]);

        doc.autoTable({
            head: [tableColumn],
            body: tableRows,
            startY: 70,
            theme: 'striped',
            headStyles: { fillColor: [44, 62, 80] },
            styles: { font: 'Roboto', fontStyle: 'normal' },
        });

        const finalY = doc.lastAutoTable.finalY;
        doc.setFontSize(12);
        doc.setFont('Roboto', 'normal');
        doc.text(`Genel Toplam:`, 160, finalY + 15, { align: 'right' });
        doc.text(`${safeNumber(purchase.total).toFixed(2)} TL`, 200, finalY + 15, { align: 'right' });

        const filename = withVisuals
            ? `HammaddeSatinAlma-${purchase.id.substring(0, 6)}.pdf`
            : `HammaddeSatinAlma-Gorselsiz-${purchase.id.substring(0, 6)}.pdf`;

        doc.save(filename);
    } catch (error) {
        console.error("Hammadde Satın Alma PDF'i oluşturulurken hata:", error);
        alert("PDF oluşturulurken bir hata oluştu: " + error.message);
    }
};

// --- HAMMADDE SATIN ALMA DETAY BİLEŞENİ ---
function RawMaterialPurchaseDetailView({ purchaseId, onBack }) {
    const { userData } = useAuth();
    const [purchase, setPurchase] = useState(null);
    const [companyInfo, setCompanyInfo] = useState(null);
    const [rawMaterials, setRawMaterials] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (!userData?.companyId || !purchaseId) return;

        const companyId = userData.companyId;

        const fetchCompanyInfo = async () => {
            const companyDocRef = doc(db, 'companies', companyId);
            const docSnap = await getDoc(companyDocRef);
            if (docSnap.exists()) setCompanyInfo(docSnap.data());
        };
        fetchCompanyInfo();

        const purchaseRef = doc(db, 'companies', companyId, 'rawMaterialPurchases', purchaseId);
        const unsubPurchase = onSnapshot(purchaseRef, (docSnap) => {
            if (docSnap.exists()) {
                setPurchase({ id: docSnap.id, ...docSnap.data() });
            }
        });

        const rawMaterialsQuery = query(collection(db, 'companies', companyId, 'products'));
        const unsubRawMaterials = onSnapshot(rawMaterialsQuery, (snapshot) => {
            setRawMaterials(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        });

        return () => { unsubPurchase(); unsubRawMaterials(); };
    }, [userData, purchaseId]);

    if (loading) return <div style={{ padding: '50px', textAlign: 'center' }}>Yükleniyor...</div>;
    if (!purchase) return <h2>Hammadde Satın Alma Kaydı Bulunamadı</h2>;

    return (
        <div style={styles.container}>
            <div onClick={onBack} style={styles.backLink}>← Listeye Dön</div>

            <div style={styles.detailContainer}>
                <div style={styles.detailHeader}>
                    <div>
                        <div style={{ fontSize: '14px', color: '#64748b', marginBottom: '4px' }}>SATIN ALMA DETAYI</div>
                        <h1 style={styles.detailTitle}>#{purchase.id.substring(0, 8).toUpperCase()}</h1>
                    </div>
                    <div style={{ display: 'flex', gap: '10px' }}>
                        <button
                            onClick={() => generateRawMaterialPurchasePDF(companyInfo, purchase, rawMaterials, true)}
                            style={{ padding: '8px 16px', borderRadius: '6px', border: '1px solid #cbd5e1', background: 'white', cursor: 'pointer' }}
                            disabled={!companyInfo || !rawMaterials.length}
                        >
                            📄 PDF İndir
                        </button>
                    </div>
                </div>

                <div style={styles.infoGrid}>
                    <div style={styles.infoItem}>
                        <span style={styles.infoLabel}>Tedarikçi Firma</span>
                        <span style={styles.infoValue}>{purchase.supplierName}</span>
                    </div>
                    <div style={styles.infoItem}>
                        <span style={styles.infoLabel}>İşlem Tarihi</span>
                        <span style={styles.infoValue}>{formatDate(purchase.purchaseDate)}</span>
                    </div>
                    <div style={styles.infoItem}>
                        <span style={styles.infoLabel}>Döviz</span>
                        <span style={styles.infoValue}>{purchase.currency || 'TRY'}</span>
                    </div>
                    {purchase.currency && purchase.currency !== 'TRY' && (
                        <div style={styles.infoItem}>
                            <span style={styles.infoLabel}>Döviz Kuru</span>
                            <span style={styles.infoValue}>1 {purchase.currency} = {purchase.exchangeRate?.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) || '-'} ₺</span>
                        </div>
                    )}
                    <div style={styles.infoItem}>
                        <span style={styles.infoLabel}>Tutar ({purchase.currency || 'TRY'})</span>
                        <span style={styles.infoValue}>{purchase.total?.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) || '-'}</span>
                    </div>
                    <div style={styles.infoItem}>
                        <span style={styles.infoLabel}>Toplam Tutar (TL)</span>
                        <span style={{ ...styles.infoValue, color: '#06C167' }}>{formatCurrency(purchase.totalTRY || purchase.total)}</span>
                    </div>
                </div>

                {purchase.note && (
                    <div style={{ marginBottom: '40px', background: '#fef3c7', padding: '20px', borderRadius: '12px', border: '1px solid #fcd34d' }}>
                        <h3 style={{ fontSize: '14px', margin: '0 0 8px 0', color: '#92400e', textTransform: 'uppercase' }}>NOT</h3>
                        <p style={{ margin: 0, color: '#78350f', fontSize: '14px', whiteSpace: 'pre-wrap' }}>{purchase.note}</p>
                    </div>
                )}

                <h3 style={{ fontSize: '16px', margin: '0 0 15px 0', color: '#334155' }}>Satın Alınan Kalemler</h3>
                <div style={{ border: '1px solid #e2e8f0', borderRadius: '8px', overflow: 'hidden' }}>
                    <table style={styles.table}>
                        <thead>
                            <tr style={{ background: '#f8fafc' }}>
                                <th style={styles.th}>Hammadde Adı</th>
                                <th style={styles.th}>Miktar</th>
                                <th style={styles.th}>Birim Fiyat</th>
                                <th style={styles.th}>Toplam</th>
                            </tr>
                        </thead>
                        <tbody>
                            {purchase.lineItems.map((item, index) => (
                                <tr key={index}>
                                    <td style={styles.td}>{item.name || item.productName || 'İsimsiz Hammadde'}</td>
                                    <td style={styles.td}>{item.quantity} {item.unit || 'Adet'}</td>
                                    <td style={styles.td}>{formatCurrency(item.price)}</td>
                                    <td style={styles.td}>{formatCurrency(item.total)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            </div>
        </div>
    );
}

// --- ANA HAMMADDE SATIN ALMA SAYFASI ---
function RawMaterialPurchases() {
    const { userData } = useAuth();
    const [purchases, setPurchases] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedPurchaseId, setSelectedPurchaseId] = useState(null);

    useEffect(() => {
        if (!userData?.companyId) {
            setLoading(false);
            return;
        }
        const companyId = userData.companyId;
        const q = query(collection(db, 'companies', companyId, 'rawMaterialPurchases'), orderBy('purchaseDate', 'desc'));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setPurchases(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
            setLoading(false);
        });
        return () => unsubscribe();
    }, [userData]);

    if (selectedPurchaseId) {
        return <RawMaterialPurchaseDetailView purchaseId={selectedPurchaseId} onBack={() => setSelectedPurchaseId(null)} />;
    }

    // Calculate Stats
    const totalPurchasesAmount = purchases.reduce((acc, curr) => acc + (curr.total || 0), 0);
    const totalPurchasesCount = purchases.length;

    // Sort by date (desc) to find last month's purchase
    // Simplification: Just allow filtering or grouping later. For now, show total.
    // Let's add functionality to show "This Month"
    const currentMonth = new Date().getMonth();
    const thisMonthPurchases = purchases.filter(p => {
        if (!p.purchaseDate) return false;
        const d = new Date(p.purchaseDate.seconds * 1000);
        return d.getMonth() === currentMonth;
    });
    const thisMonthAmount = thisMonthPurchases.reduce((acc, curr) => acc + (curr.total || 0), 0);


    return (
        <div style={styles.container}>
            <div style={styles.header}>
                <h1 style={styles.title}>Hammadde Satın Alma</h1>
                <Link to="/hammadde-satin-alma/yeni" style={styles.actionButton}>
                    <span>+</span> Yeni Satın Alma
                </Link>
            </div>

            {/* STAT CARDS */}
            <div style={styles.statsContainer}>
                <div style={styles.statCard}>
                    <div style={{ ...styles.statIcon, background: '#dbeafe', color: '#2563eb' }}>📦</div>
                    <div style={styles.statTitle}>Toplam İşlem</div>
                    <div style={styles.statValue}>{totalPurchasesCount}</div>
                </div>
                <div style={styles.statCard}>
                    <div style={{ ...styles.statIcon, background: '#dcfce7', color: '#06C167' }}>💰</div>
                    <div style={styles.statTitle}>Toplam Harcama</div>
                    <div style={{ ...styles.statValue, color: '#06C167' }}>{formatCurrency(totalPurchasesAmount)}</div>
                </div>
                <div style={styles.statCard}>
                    <div style={{ ...styles.statIcon, background: '#f3e8ff', color: '#9333ea' }}>📅</div>
                    <div style={styles.statTitle}>Bu Ayki Harcama</div>
                    <div style={{ ...styles.statValue, color: '#9333ea' }}>{formatCurrency(thisMonthAmount)}</div>
                </div>
            </div>

            <div style={styles.box}>
                <div style={styles.tableHeader}>
                    <h2 style={styles.tableTitle}>Son İşlemler</h2>
                    {/* Add search filter here if needed */}
                </div>
                {loading ? <div style={{ padding: '40px', textAlign: 'center' }}>Yükleniyor...</div> : (
                    <table style={styles.table}>
                        <thead>
                            <tr>
                                <th style={styles.th}>Kayıt No</th>
                                <th style={styles.th}>Tedarikçi</th>
                                <th style={styles.th}>Tarih</th>
                                <th style={styles.th}>Kalem Sayısı</th>
                                <th style={styles.th}>Tutar</th>
                                <th style={styles.th}>Durum</th>
                            </tr>
                        </thead>
                        <tbody>
                            {purchases.length > 0 ? purchases.map(purchase => (
                                <tr
                                    key={purchase.id}
                                    style={styles.tr}
                                    onClick={() => setSelectedPurchaseId(purchase.id)}
                                    onMouseEnter={(e) => e.currentTarget.style.background = '#f8fafc'}
                                    onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
                                >
                                    <td style={{ ...styles.td, fontWeight: '600', color: '#06C167' }}>#{purchase.id.substring(0, 6).toUpperCase()}</td>
                                    <td style={{ ...styles.td, fontWeight: '500' }}>{purchase.supplierName}</td>
                                    <td style={styles.td}>{formatDate(purchase.purchaseDate)}</td>
                                    <td style={styles.td}>{purchase.lineItems?.length || 0}</td>
                                    <td style={styles.td}>
                                        {purchase.currency && purchase.currency !== 'TRY' ? (
                                            <span style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                                                <span style={{ fontSize: '12px', color: '#64748b' }}>{purchase.total?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} {purchase.currency}</span>
                                                <span style={{ fontWeight: '600', color: '#0f172a' }}>{formatCurrency(purchase.totalTRY || purchase.total)}</span>
                                            </span>
                                        ) : (
                                            <span style={{ fontWeight: '600', color: '#0f172a' }}>{formatCurrency(purchase.totalTRY || purchase.total)}</span>
                                        )}
                                    </td>
                                    <td style={styles.td}>
                                        <span style={styles.statusBadge}>Tamamlandı</span>
                                    </td>
                                </tr>
                            )) : (
                                <tr><td colSpan="6" style={{ ...styles.td, textAlign: 'center', padding: '40px', color: '#94a3b8' }}>Henüz hammadde satın alma kaydı bulunmuyor.</td></tr>
                            )}
                        </tbody>
                    </table>
                )}
            </div>
        </div>
    );
}

export default RawMaterialPurchases;